/**
 * The nutrition tool's macro math, shared with "meals for your macros" on
 * /meals so both give the same numbers: BMR from the visitor's answers, then
 * calories and macros for each goal. The calculator page adds each goal's
 * translated description. No imports, so any page (and a test) can use it.
 */

export const feetToCentimeters = (feet, inches = 0) => {
  const totalInches = feet * 12 + inches;
  const centimeters = totalInches * 2.54;
  return Math.round(centimeters);
};

// Calories a day on top of maintenance (the goal's pace).
export const MACRO_GOALS = [
  { key: 'rapid-weight-loss', adjustment: -1000 },
  { key: 'moderate-weight-loss', adjustment: -500 },
  { key: 'slow-weight-loss', adjustment: -250 },
  { key: 'maintain-weight', adjustment: 0 },
  { key: 'slow-weight-gain', adjustment: 250 },
  { key: 'moderate-weight-gain', adjustment: 500 },
  { key: 'rapid-weight-gain', adjustment: 1000 },
];

// Shares of calories from [protein, carbs, fat].
export const MACRO_RATIOS = {
  balanced: [0.35, 0.35, 0.3],
  'low-carb': [0.4, 0.2, 0.4],
  'high-carb': [0.3, 0.5, 0.2],
  'high-protein': [0.4, 0.3, 0.3],
  ketogenic: [0.4, 0.1, 0.5],
};

/**
 * answers: { unit 'imperial' | 'metric', gender, weight (lb | kg), age,
 * height ("5'10" | cm), bodyFatPercentage, bmrFormula }.
 */
export function calculateBMR(macroAnswers) {
  let feet, inches;
  let heightInCm;

  if (macroAnswers.unit === 'imperial') {
    [feet, inches] = macroAnswers.height.split("'");
    heightInCm = feetToCentimeters(Number(feet), Number(inches));
  } else if (macroAnswers.unit === 'metric') {
    heightInCm = Number(macroAnswers.height);
  }

  let bmrFormula = macroAnswers.bmrFormula;
  let weight =
    macroAnswers.unit === 'imperial'
      ? Number(macroAnswers.weight) * 0.453592
      : macroAnswers.weight;
  let age = Number(macroAnswers.age);
  let lbm = weight * (1 - macroAnswers.bodyFatPercentage / 100);
  let bmr = 0;

  if (macroAnswers.gender === 'male') {
    if (bmrFormula === 'mifflin-st-jeor') {
      // Mifflin-St Jeor (Recommended):
      bmr = 10 * weight + 6.25 * heightInCm - 5 * age + 5;
    } else if (bmrFormula === 'harris-benedict') {
      // Harris-Benedict:
      bmr = 88.362 + 13.397 * weight + 4.799 * heightInCm - 5.677 * age;
    } else {
      // Katch-McArdle:
      bmr = 370 + 21.6 * lbm;
    }
  } else if (macroAnswers.gender === 'female') {
    if (bmrFormula === 'mifflin-st-jeor') {
      // Mifflin-St Jeor (Recommended):
      bmr = 10 * weight + 6.25 * heightInCm - 5 * age - 161;
    } else if (bmrFormula === 'harris-benedict') {
      // Harris-Benedict:
      bmr = 447.593 + 9.247 * weight + 3.098 * heightInCm - 4.33 * age;
    } else {
      // Katch-McArdle:
      bmr = 370 + 21.6 * lbm;
    }
  }
  return bmr;
}

/** Grams from calories and [protein, carbs, fat] shares. */
export const macrosFor = (calories, [proteinPercent, carbPercent, fatPercent]) => ({
  protein: Math.round((proteinPercent * calories) / 4),
  carbs: Math.round((carbPercent * calories) / 4),
  fat: Math.round((fatPercent * calories) / 9),
});

/**
 * { bmr, maintenanceCalories, macrosForEachGoal: { [goal]: { calories,
 * protein, carbs, fat, isSelected } } }, daily figures. macroPreference
 * picks the split (balanced unless set); 'custom' uses customMacros (%).
 */
export function calculateMacroTargets(
  bmr,
  { activityFactor, macroPreference, goal, customMacros } = {},
) {
  const maintenanceCalories = Math.round(bmr * activityFactor);
  const ratios =
    macroPreference === 'custom' && customMacros
      ? [customMacros.protein / 100, customMacros.carbs / 100, customMacros.fat / 100]
      : MACRO_RATIOS[macroPreference] || MACRO_RATIOS.balanced;

  const macrosForEachGoal = {};
  MACRO_GOALS.forEach(({ key, adjustment }) => {
    const calories = maintenanceCalories + adjustment;
    macrosForEachGoal[key] = {
      calories,
      ...macrosFor(calories, ratios),
      isSelected: key === goal,
    };
  });

  return { bmr: Math.round(bmr), maintenanceCalories, macrosForEachGoal };
}
